// @ts-nocheck
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users,
  BookOpen,
  Calendar,
  Settings,
  File,
  Plus,
  Clock,
  Check,
  Loader,
  AlertTriangle,
  X
} from "lucide-react";
import { AnimatedMetricCard } from "./AnimatedMetricCard";
import { InteractiveCard } from "./InteractiveCard";
import { CaseCard } from "./cases/CaseCard";
import MiniCalendar from "./MiniCalendar";
import { useEffect, useState } from "react";
import { supabase } from "../integrations/supabase/client";
import { Skeleton } from "./ui/skeleton";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast"; // Importar useToast
import { InactiveCaseAlert } from "@/components/ui/InactiveCaseAlert"; // Importar InactiveCaseAlert

// Definindo uma interface para o caso, que seja compatível com CaseCard
interface CaseForDashboard {
  id: string;
  case_number?: string;
  title: string;
  description?: string;
  case_type: string;
  status: string;
  priority: string;
  deadline?: string;
  lawyer_responsible?: string;
  created_at: string;
  client_id?: string;
  clients?: {
    name: string;
    email: string;
  };
}

interface InactiveCase {
  case_id: string;
  case_title: string;
  client_id: string;
  client_name: string;
  client_age: number;
  inactivity_days: number;
  last_movement_date: string;
  priority_status: string;
}

/** Data local YYYY-MM-DD para limitar avisos de “data pendente” a uma vez por dia. */
function getLocalDateKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const DASHBOARD_PENDING_DATE_ALERTS_SHOWN_KEY = "dashboard_pendingDateAlerts_shownDate";

export const Dashboard = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { toast } = useToast(); // Inicializar useToast
  const [metrics, setMetrics] = useState<any[]>([]);
  const [recentCases, setRecentCases] = useState<CaseForDashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [inactiveAlerts, setInactiveAlerts] = useState<InactiveCase[]>([]); // Novo estado para alertas de inatividade
  const [showInactiveAlerts, setShowInactiveAlerts] = useState(true); // Estado para controlar a exibição dos alertas
  const [criticalDeadlines, setCriticalDeadlines] = useState<any[]>([]); // Novo estado para prazos críticos
  const [recentActivities, setRecentActivities] = useState<any[]>([]); // Novo estado para atividades recentes
  const [pendingDateAlerts, setPendingDateAlerts] = useState<any[]>([]); // Novo estado para alertas de datas pendentes
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set()); // Estado para controlar alertas fechados
  const [suppressPendingDateAlertsForToday, setSuppressPendingDateAlertsForToday] = useState(() => {
    try {
      return localStorage.getItem(DASHBOARD_PENDING_DATE_ALERTS_SHOWN_KEY) === getLocalDateKey();
    } catch {
      return false;
    }
  });

  useEffect(() => {
    // Carregar a configuração do localStorage
    const savedSettings = localStorage.getItem('userSettings');
    if (savedSettings) {
      const parsedSettings = JSON.parse(savedSettings);
      if (typeof parsedSettings.showInactiveAlerts === 'boolean') {
        setShowInactiveAlerts(parsedSettings.showInactiveAlerts);
      }
    }

    const fetchDashboardData = async () => {
      setLoading(true);

      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const sevenDaysFromNow = new Date();
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);

      // Buscando as métricas
      const { count: activeCasesCount } = await supabase
        .from('cases')
        .select('id', { count: 'exact', head: true })
        .not('status', 'in', '("Transitado em julgado", "Arquivado", "Suspenso")')
        .is('archived', false); // Inclui apenas processos não arquivados

      const { count: activeAdministrativeCount } = await supabase
        .from('administrative_processes')
        .select('id', { count: 'exact', head: true })
        .not('status', 'in', '("Fechado", "Concluído", "Arquivado")');

      const { count: clientsCount } = await supabase
        .from('clients')
        .select('id', { count: 'exact', head: true });

      // Buscar todas as tarefas e filtrar no frontend
      const { data: allTasks } = await supabase
        .from('tasks')
        .select('id, due_date');
      
      const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const sevenDaysLocal = new Date(sevenDaysFromNow.getFullYear(), sevenDaysFromNow.getMonth(), sevenDaysFromNow.getDate());
      
      const upcomingDeadlinesCount = allTasks?.filter(task => {
        if (!task.due_date) return false;
        
        const taskDate = new Date(task.due_date);
        const taskDateLocal = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
        
        return taskDateLocal.getTime() >= todayLocal.getTime() && taskDateLocal.getTime() <= sevenDaysLocal.getTime();
      }).length || 0;

      const { data: monthlyRevenueData, error: revenueError } = await supabase
        .from('financial_entries')
        .select('amount')
        .eq('entry_type', 'Receita')
        .gte('payment_date', firstDayOfMonth.toISOString());

      const monthlyRevenue = monthlyRevenueData?.reduce((acc, entry) => acc + entry.amount, 0) ?? 0;
        
      // Buscando os casos recentes
      const { data: recentCasesData, error: recentCasesError } = await supabase
        .from('cases')
        .select(`
          id,
          case_number,
          title,
          status,
          priority,
          created_at,
          case_type,
          clients ( name, email )
        `)
        .order('created_at', { ascending: false })
        .limit(3);

      // Buscar e exibir alertas de inatividade
      let inactiveCasesData: InactiveCase[] = []; // Inicializa com um array vazio
      const { data: user } = await supabase.auth.getUser();
      if (user.user) {
        // Temporarily disabled until function is available
        // const { data, error: inactiveCasesError } = await supabase.rpc('get_inactive_priority_cases', { p_user_id: user.user.id });
        const data = null; // Temporarily disabled
        const inactiveCasesError = null;
        if (inactiveCasesError) {
          console.error("Erro ao buscar processos inativos:", inactiveCasesError);
          toast({
            title: "Erro",
            description: "Não foi possível carregar alertas de inatividade.",
            variant: "destructive"
          });
        } else {
          inactiveCasesData = data || []; // Atribui os dados ou um array vazio
          setInactiveAlerts(inactiveCasesData); // Atualiza o estado dos alertas
        }
      }

      // Buscar processos administrativos com datas de recebimento pendentes
      if (user.user) {
        const { data: pendingDateData, error: pendingDateError } = await supabase
          .from('administrative_processes')
          .select(`
            id,
            title,
            client_name,
            fee_configuration
          `)
          .eq('user_id', user.user.id)
          .not('fee_configuration', 'is', null);

        if (pendingDateError) {
          console.error("Erro ao buscar processos com datas pendentes:", pendingDateError);
        } else {
          // Filtrar processos que têm benefitFirstPaymentDatePending = true
          const pendingProcesses = (pendingDateData || []).filter(process => {
            const config = process.fee_configuration;
            return config && 
                   config.benefitFirstPaymentDatePending === true && 
                   config.hasSuccessFee === true &&
                   config.benefitValue > 0;
          });
          
          setPendingDateAlerts(pendingProcesses);
        }
      }

      // Buscando prazos críticos
      const threeDaysFromNow = new Date(today);
      threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
      const threeDaysLocal = new Date(threeDaysFromNow.getFullYear(), threeDaysFromNow.getMonth(), threeDaysFromNow.getDate());

      const { data: allTasksForCritical } = await supabase
        .from('tasks')
        .select('*')
        .order('due_date', { ascending: true });

      const criticalDeadlinesData = allTasksForCritical?.filter(task => {
        if (!task.due_date) return false;
        
        const taskDate = new Date(task.due_date);
        const taskDateLocal = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
        
        return taskDateLocal.getTime() >= todayLocal.getTime() && taskDateLocal.getTime() <= threeDaysLocal.getTime();
      }).slice(0, 5) || [];

      setCriticalDeadlines(criticalDeadlinesData);

      // Buscar atividades reais recentes
      const { data: recentActivitiesData } = await supabase
        .from('cases')
        .select('id, title, created_at, status')
        .eq('user_id', user.user?.id)
        .order('created_at', { ascending: false })
        .limit(3);

      const realActivities = recentActivitiesData?.map((case_, index) => ({
        id: case_.id,
        type: "Processo",
        description: case_.title,
        date: case_.created_at
      })) || [];

      setRecentActivities(realActivities);

      const fetchedMetrics = [
        {
          title: "Processos Judiciais",
          value: String(activeCasesCount ?? 0),
          description: "Casos judiciais em andamento",
          icon: BookOpen,
          trend: "up" as const,
          chartData: [
            { value: 10 }, { value: 12 }, { value: 15 }, { value: 18 },
            { value: activeCasesCount ?? 0 }
          ],
          onClick: () => navigate('/cases'),
          interactive: true,
        },
        {
          title: "Proc. Administrativos",
          value: String(activeAdministrativeCount ?? 0),
          description: "Processos administrativos em andamento",
          icon: File,
          trend: "up" as const,
          chartData: [
            { value: 5 }, { value: 7 }, { value: 9 }, { value: 11 },
            { value: activeAdministrativeCount ?? 0 }
          ],
          onClick: () => navigate('/cases#adm'),
          interactive: true,
        },
        {
          title: "Clientes Ativos",
          value: String(clientsCount ?? 0),
          description: "Total de clientes",
          icon: Users,
          trend: "up" as const,
          chartData: [
            { value: 5 }, { value: 7 }, { value: 9 }, { value: 11 },
            { value: clientsCount ?? 0 }
          ],
          onClick: () => navigate('/clients'),
          interactive: true,
        },
        {
          title: "Prazos Próximos",
          value: String(upcomingDeadlinesCount ?? 0),
          description: "Nos próximos 7 dias",
          icon: Clock,
          trend: "neutral" as const,
          chartData: [
            { value: 2 }, { value: 3 }, { value: 2 }, { value: 4 },
            { value: upcomingDeadlinesCount ?? 0 }
          ],
          onClick: () => navigate('/tasks'),
          interactive: true,
        },
        {
            title: "Receita Mensal",
            value: `R$ ${monthlyRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            description: "Receita do mês atual",
            icon: Calendar,
            trend: "up" as const,
            chartData: [
              { value: 1000 }, { value: 1200 }, { value: 1500 }, { value: 1800 },
              { value: monthlyRevenue }
            ],
            onClick: () => navigate('/financial'),
            interactive: true,
        },
        {
          title: "Alertas de Inatividade",
          value: String(inactiveCasesData.length),
          description: "Processos prioritários inativos",
          icon: AlertTriangle,
          trend: "down" as const, // Indica que é algo a ser resolvido
          chartData: [
            { value: 5 }, { value: 4 }, { value: 6 }, { value: 3 },
            { value: inactiveCasesData.length }
          ],
          onClick: () => navigate('/cases'),
          interactive: true,
        },
        {
          title: "Datas Pendentes",
          value: String(pendingDateAlerts.length),
          description: "Processos administrativos aguardando data",
          icon: Clock,
          trend: "down" as const, // Indica que é algo a ser resolvido
          chartData: [
            { value: 2 }, { value: 3 }, { value: 1 }, { value: 2 },
            { value: pendingDateAlerts.length }
          ],
          onClick: () => navigate('/cases'),
          interactive: true,
        }
      ];

      setMetrics(fetchedMetrics);
      
      if (recentCasesData) {
        setRecentCases(recentCasesData as CaseForDashboard[]);
      }

      if(recentCasesError || revenueError) {
        console.error("Erro ao buscar dados do dashboard:", { recentCasesError, revenueError });
      }

      setLoading(false);
    };

    fetchDashboardData();
  }, []);

  // Marca o dia após o primeiro paint com avisos visíveis (setTimeout + cleanup evita Strict Mode marcar antes do remount).
  useEffect(() => {
    const visible = pendingDateAlerts.filter((p) => !dismissedAlerts.has(p.id));
    if (visible.length === 0 || suppressPendingDateAlertsForToday) return;
    try {
      if (localStorage.getItem(DASHBOARD_PENDING_DATE_ALERTS_SHOWN_KEY) === getLocalDateKey()) return;
    } catch {
      return;
    }
    const day = getLocalDateKey();
    const t = window.setTimeout(() => {
      try {
        localStorage.setItem(DASHBOARD_PENDING_DATE_ALERTS_SHOWN_KEY, day);
      } catch {
        /* ignore */
      }
    }, 0);
    return () => clearTimeout(t);
  }, [pendingDateAlerts, dismissedAlerts, suppressPendingDateAlertsForToday]);

  // Ações rápidas
  const quickActions = [
    {
      title: "Novo Caso Urgente",
      description: "Cadastrar um novo caso com prioridade alta.",
      icon: Plus,
      category: "Urgente",
      status: "active" as const,
      featured: true,
      onClick: () => navigate('/cases')
    },
    {
      title: "Relatório Mensal",
      description: "Gerar relatório de atividades e resultados.",
      icon: File,
      category: "Relatório",
      status: "pending" as const,
      progress: 73,
      onClick: () => navigate('/reports')
    },
    {
      title: "Agenda da Semana",
      description: "Visualizar e organizar compromissos.",
      icon: Calendar,
      category: "Agenda",
      status: "active" as const,
      progress: 45,
      onClick: () => navigate('/agenda')
    }
  ];

  // Funções vazias para o CaseCard
  const handleAction = () => {
    console.log("Ação não implementada no dashboard");
  };

  const handleViewCase = (caseId: string) => {
    navigate(`/processo/${caseId}`);
  };

  const handleCaseCardClick = (case_: CaseForDashboard) => {
    navigate(`/processo/${case_.id}`);
  };

  const handleCloseAlert = (id: string) => {
    setInactiveAlerts(prev => prev.filter(alert => alert.case_id !== id));
  };

  return (
    <div className="p-2 xs:p-3 sm:p-4 md:p-6 min-h-full relative ios-no-bounce">
      {/* Cabeçalho */}
      <header className="mb-6 flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-1 duration-300 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9a7f52]">
            {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
            {(() => {
              const h = new Date().getHours();
              const greeting = h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
              const first = String(profile?.full_name || '').split(' ')[0];
              return first ? `${greeting}, Dra. ${first}` : greeting;
            })()}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {(() => {
              const h = new Date().getHours();
              const moment = h < 12 ? 'começar o dia' : h < 18 ? 'seguir a tarde' : 'fechar o dia';
              return `Um resumo do escritório para ${moment}.`;
            })()}
          </p>
        </div>
        <Button onClick={() => navigate('/cases')} className="h-10 rounded-xl bg-slate-900 px-4 text-[13px] text-white hover:bg-slate-800">
          <Plus className="mr-1.5 h-4 w-4 text-[#e8d5a8]" />
          Novo processo
        </Button>
      </header>

      {/* Indicadores principais */}
      {loading ? (
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-36 w-full rounded-2xl" />
          ))}
        </div>
      ) : (() => {
        const byTitle = (title: string) => metrics.find((m) => m.title === title);
        const portfolio = [
          { metric: byTitle('Processos Judiciais'), label: 'Processos judiciais', link: 'Ver processos' },
          { metric: byTitle('Proc. Administrativos'), label: 'Processos administrativos', link: 'Ver administrativos' },
          { metric: byTitle('Clientes Ativos'), label: 'Clientes', link: 'Ver clientes' },
        ].filter((item) => item.metric);
        const revenue = byTitle('Receita Mensal');
        const RevenueIcon = revenue?.icon;
        const attention = [
          { metric: byTitle('Prazos Próximos'), label: 'Prazos nos próximos 7 dias', tone: 'amber' },
          { metric: byTitle('Alertas de Inatividade'), label: 'Processos prioritários parados', tone: 'red' },
          { metric: byTitle('Datas Pendentes'), label: 'Administrativos sem data', tone: 'amber' },
        ].filter((item) => item.metric);

        return (
          <div className="mb-8 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {portfolio.map(({ metric, label, link }, index) => {
                const Icon = metric!.icon;
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={metric!.onClick}
                    className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 text-left transition-all duration-300 animate-in fade-in slide-in-from-bottom-2 fill-mode-both hover:-translate-y-0.5 hover:border-[#c4ab83]/60 hover:shadow-[0_12px_32px_-16px_rgba(15,23,42,0.25)]"
                    style={{ animationDelay: `${index * 60}ms` }}
                  >
                    {/* Filete dourado discreto no topo, que se estende ao passar o mouse */}
                    <span className="pointer-events-none absolute left-5 top-0 h-[2px] w-10 rounded-b-full bg-gradient-to-r from-[#c4ab83] to-[#e8d5a8] transition-all duration-500 group-hover:w-[calc(100%-2.5rem)]" />
                    <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-[#c4ab83]/15 text-[#9a7f52]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <p className="relative mt-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</p>
                    <p className="relative mt-1 text-4xl font-semibold tabular-nums tracking-tight text-slate-900">{metric!.value}</p>
                    <p className="relative mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#9a7f52]">
                      {link}
                      <span className="transition-transform duration-200 group-hover:translate-x-0.5">→</span>
                    </p>
                  </button>
                );
              })}

              {revenue && RevenueIcon && (
                <button
                  type="button"
                  onClick={revenue.onClick}
                  className="group relative overflow-hidden rounded-2xl bg-slate-900 p-5 text-left text-white transition-all duration-300 animate-in fade-in slide-in-from-bottom-2 fill-mode-both hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-16px_rgba(15,23,42,0.6)]"
                  style={{ animationDelay: '180ms' }}
                >
                  <img
                    src="/imgs/logos allana/letra_A_logo.png"
                    alt=""
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-6 -right-4 h-32 w-32 select-none object-contain opacity-[0.15]"
                  />
                  <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-[#e8d5a8]">
                    <RevenueIcon className="h-5 w-5" />
                  </div>
                  <p className="relative mt-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/50">
                    Receita de {new Date().toLocaleDateString('pt-BR', { month: 'long' })}
                  </p>
                  <p className="relative mt-1 text-3xl font-semibold tabular-nums tracking-tight text-[#e8d5a8]">{revenue.value}</p>
                  <p className="relative mt-3 inline-flex items-center gap-1 text-xs font-medium text-white/60">
                    Abrir financeiro
                    <span className="transition-transform duration-200 group-hover:translate-x-0.5">→</span>
                  </p>
                </button>
              )}
            </div>

            {attention.length > 0 && (
              <div
                className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white animate-in fade-in slide-in-from-bottom-2 fill-mode-both"
                style={{ animationDelay: '240ms' }}
              >
                <div className="border-b border-slate-100 px-5 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Precisa de atenção</p>
                </div>
                <div className="grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                  {attention.map(({ metric, label, tone }) => {
                    const count = Number(metric!.value) || 0;
                    const alert = count > 0;
                    const color = tone === 'red' ? 'text-red-600' : 'text-amber-600';
                    const dot = tone === 'red' ? 'bg-red-500' : 'bg-amber-500';
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={metric!.onClick}
                        className="group flex items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-[#faf7f2]"
                      >
                        <span className={`text-3xl font-semibold tabular-nums tracking-tight ${alert ? color : 'text-slate-300'}`}>{count}</span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-slate-800">{label}</span>
                          {alert ? (
                            <span className={`mt-0.5 inline-flex items-center gap-1.5 text-xs font-medium ${color}`}>
                              <span className={`h-1.5 w-1.5 animate-pulse rounded-full ${dot}`} /> Verificar
                            </span>
                          ) : (
                            <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-emerald-600">✓ Em dia</span>
                          )}
                        </span>
                        <span className="text-slate-300 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-[#9a7f52]">→</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Seção de Ações Rápidas */}
      <div className="mb-4 xs:mb-6 sm:mb-8 ios-animate-slide-up" style={{ animationDelay: '400ms' }}>
        <h2 className="text-lg xs:text-xl sm:text-2xl font-semibold mb-3 xs:mb-4 sm:mb-6 text-foreground ios-text-responsive">Ações Rápidas</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-3 xs:gap-4 sm:gap-6">
          {quickActions.map((action, index) => (
            <div key={action.title} className="ios-card-responsive">
              <InteractiveCard
                {...action}
                delay={400 + (index * 100)}
                onClick={action.onClick}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Grid Principal */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 xs:gap-4 sm:gap-6 lg:gap-8 ios-grid-responsive">
        {/* Processos Recentes Modernizados */}
        <div className="lg:col-span-1 xl:col-span-2 ios-animate-slide-up" style={{ animationDelay: '600ms' }}>
          <Card className="bg-white shadow-lg border border-gray-200 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 group overflow-hidden relative ios-card">
            <CardHeader className="relative bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg p-3 xs:p-4 sm:p-6">
              <div className="flex flex-col xs:flex-row items-start xs:items-center justify-between gap-2 xs:gap-4">
                <CardTitle className="text-lg xs:text-xl sm:text-xl flex items-center gap-2 text-black font-bold ios-text-responsive">
                  <div className="p-1.5 xs:p-2 bg-gradient-to-br from-[#c4ab83] to-[#b49a73] rounded-lg shadow-md">
                    <File className="w-4 h-4 xs:w-5 xs:h-5 text-white" />
                  </div>
                  Processos Recentes
                  <Badge variant="outline" className="ml-1 xs:ml-2 bg-[#c4ab83]/20 border-[#c4ab83] text-[#c4ab83] font-medium text-xs xs:text-sm">
                    {loading ? <Loader className="w-3 h-3 xs:w-4 xs:h-4 animate-spin" /> : recentCases.length}
                  </Badge>
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="hover:bg-[#c4ab83]/20 hover:text-[#c4ab83] hover:border-[#c4ab83] transition-colors ios-touch-target ios-no-highlight text-xs xs:text-sm"
                  onClick={() => navigate('/cases')}
                >
                  Ver Todos
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0 relative z-10">
              {loading ? (
                <div className="p-4 space-y-4">
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                </div>
              ) : (
                <div className="space-y-0">
                  {recentCases.map((case_) => (
                    <CaseCard 
                      key={case_.id}
                      case_={case_}
                      onEdit={() => handleAction()}
                      onDelete={() => handleAction()}
                      onAnalyze={() => handleAction()}
                      onClick={handleCaseCardClick}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar Modernizada - Mini Calendário e Atividades */}
        <div className="lg:col-span-1 xl:col-span-1 space-y-3 xs:space-y-4 sm:space-y-6 ios-animate-slide-up" style={{ animationDelay: '800ms' }}>
          <div className="ios-card-responsive">
            <MiniCalendar />
          </div>
          <Card 
            className="bg-white shadow-lg border border-gray-200 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 group overflow-hidden relative cursor-pointer ios-card"
            onClick={() => navigate('/agenda')}
          >
            <CardHeader className="relative bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg p-3 xs:p-4 sm:p-6">
              <CardTitle className="text-base xs:text-lg flex items-center gap-2 text-black font-bold ios-text-responsive">
                <div className="p-1.5 xs:p-2 bg-gradient-to-br from-[#c4ab83] to-[#b49a73] rounded-lg shadow-md">
                  <Clock className="w-3 h-3 xs:w-4 xs:h-4 text-white" />
                </div>
                Atividade Recente
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 xs:p-4 space-y-2 xs:space-y-3 bg-gray-50/30">
              {recentActivities.length > 0 ? (
                <ul className="space-y-1 xs:space-y-2">
                  {recentActivities.map((activity: any) => (
                    <li key={activity.id} className="flex items-center justify-between text-xs xs:text-sm text-muted-foreground ios-touch-target">
                      <span className="font-medium text-foreground truncate">{activity.type}: {activity.description}</span>
                      <Badge variant="outline" className="bg-secondary/10 text-secondary text-xs ml-1 xs:ml-2 flex-shrink-0">
                        {new Date(activity.date).toLocaleDateString('pt-BR')}
                      </Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs xs:text-sm text-muted-foreground">Nenhuma atividade recente.</p>
              )}
            </CardContent>
          </Card>

          <Card 
            className="bg-white shadow-lg border border-gray-200 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 group overflow-hidden relative cursor-pointer ios-card"
            onClick={() => navigate('/tasks')}
          >
            <CardHeader className="relative bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg p-3 xs:p-4 sm:p-6">
              <CardTitle className="text-base xs:text-lg flex items-center gap-2 text-black font-bold ios-text-responsive">
                <div className="p-1.5 xs:p-2 bg-gradient-to-br from-[#c4ab83] to-[#b49a73] rounded-lg shadow-md">
                  <Clock className="w-3 h-3 xs:w-4 xs:h-4 text-white animate-bounce-soft" />
                </div>
                Prazos Críticos
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 xs:p-4 space-y-2 xs:space-y-3 bg-gray-50/30">
              {criticalDeadlines.length > 0 ? (
                <ul className="space-y-1 xs:space-y-2">
                  {criticalDeadlines.map((deadline: any) => (
                    <li key={deadline.id} className="flex items-center justify-between text-xs xs:text-sm text-muted-foreground ios-touch-target">
                      <span className="font-medium text-foreground truncate">{deadline.title}</span>
                      <Badge variant="outline" className="bg-warning/10 text-warning text-xs ml-1 xs:ml-2 flex-shrink-0">
                        {new Date(deadline.due_date).toLocaleDateString('pt-BR')}
                      </Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs xs:text-sm text-muted-foreground">Nenhum prazo crítico encontrado.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Renderiza os alertas interativos */}
      {showInactiveAlerts && (
        <div className="fixed bottom-2 xs:bottom-4 right-2 xs:right-4 space-y-1 xs:space-y-2 z-50">
          {inactiveAlerts.map((alert) => (
            <InactiveCaseAlert
              key={alert.case_id}
              id={alert.case_id}
              clientName={alert.client_name}
              caseTitle={alert.case_title}
              inactivityDays={alert.inactivity_days}
              onViewCase={handleViewCase}
              onClose={handleCloseAlert}
            />
          ))}
        </div>
      )}
    </div>
  );
};